import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Server,
  Cloud,
  Shield,
  Code,
  Cpu,
  Globe,
  Settings,
  Bot,
  ChevronUp,
} from 'lucide-react';
import FloatingShapes from './FloatingShapes';

const Contact = () => {
  const sections = useRef([]);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observers = [];
    sections.current.forEach((section) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeInScale');
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.1 }
      );
      if (section) observer.observe(section);
      observers.push(observer);
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  // Native touch scroll is used — no override needed

   const hoverVariants = {
    hover: { scale: 1.05, transition: { duration: 0.3 } },
    tap: { scale: 0.98 }
  };

 const icons = [
   { emoji: '💻', from: { x: '5vw', y: '20vh' }, to: { x: '15vw', y: '10vh' }, delay: 0 },
   { emoji: '☁️', from: { x: '80vw', y: '30vh' }, to: { x: '70vw', y: '20vh' }, delay: 0.4 },
   { emoji: '🔒', from: { x: '10vw', y: '70vh' }, to: { x: '20vw', y: '60vh' }, delay: 0.8 },
   { emoji: '⚙️', from: { x: '60vw', y: '80vh' }, to: { x: '50vw', y: '70vh' }, delay: 1.2 },
   { emoji: '🤖', from: { x: '40vw', y: '50vh' }, to: { x: '60vw', y: '45vh' }, delay: 1.6 },
   { emoji: '🌐', from: { x: '30vw', y: '10vh' }, to: { x: '40vw', y: '20vh' }, delay: 2 },
 ];
 
const techIcons = [
  { Icon: Server, from: { x: '10vw', y: '15vh' }, to: { x: '20vw', y: '10vh' }, delay: 0 },
  { Icon: Cloud, from: { x: '85vw', y: '25vh' }, to: { x: '75vw', y: '20vh' }, delay: 0.3 },
  { Icon: Shield, from: { x: '5vw', y: '75vh' }, to: { x: '15vw', y: '65vh' }, delay: 0.6 },
  { Icon: Code, from: { x: '70vw', y: '85vh' }, to: { x: '60vw', y: '75vh' }, delay: 0.9 },
  { Icon: Cpu, from: { x: '45vw', y: '55vh' }, to: { x: '55vw', y: '50vh' }, delay: 1.2 },
  { Icon: Globe, from: { x: '25vw', y: '15vh' }, to: { x: '35vw', y: '25vh' }, delay: 1.5 },
  { Icon: Settings, from: { x: '75vw', y: '70vh' }, to: { x: '65vw', y: '60vh' }, delay: 1.8 },
  { Icon: Bot, from: { x: '30vw', y: '40vh' }, to: { x: '40vw', y: '30vh' }, delay: 2.1 },
];

  return (
    <div className="min-h-screen bg-gray-950">
      <section
        ref={(el) => (sections.current[0] = el)}
        className="relative min-h-[850px] text-white flex items-center justify-center text-center overflow-hidden opacity-0 py-20"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 to-blue-900/90 z-0"></div>
     

{icons.map((icon, index) => (
  <motion.div
    key={index}
    className="absolute text-3xl sm:text-4xl opacity-40 select-none z-0"
    style={{ left: icon.from.x, top: icon.from.y }}
    animate={{
      left: icon.to.x,
      top: icon.to.y,
      opacity: [0.4, 0.8, 0.4],
      rotate: [0, 10, -10, 0],
    }}
    transition={{
      duration: 6,
      delay: icon.delay,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'easeInOut',
    }}
  >
    {icon.emoji}
  </motion.div>
))}

{techIcons.map(({ Icon, from, to, delay }, index) => (
  <motion.div
    key={index}
    className="absolute text-white opacity-30 z-0"
    style={{ left: from.x, top: from.y }}
    animate={{
      left: to.x,
      top: to.y,
      opacity: [0.3, 0.6, 0.3],
      rotate: [0, 15, -15, 0],
    }}
    transition={{
      repeat: Infinity,
      repeatType: 'reverse',
      duration: 6,
      delay,
      ease: 'easeInOut',
    }}
  >
    <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
  </motion.div>
))}


        <div className="relative z-10 px-6 sm:px-10 lg:px-24 max-w-5xl">
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-8 text-shadow-lg text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Contact CmCoreTech
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-gray-100 mb-10 leading-relaxed text-shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Connect with us for inquiries, consultations, or tailored IT solutions.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-6 mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a
              href="#contact"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-4 rounded-lg shadow-xl transition duration-300"
            >
              Request a Consultation
            </a>
            <a
              href="#services"
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-7 py-4 rounded-lg shadow-xl transition duration-300"
            >
              Explore Services
            </a>
          </motion.div>
      
        </div>
      </section>

      {/* ══════════════════════════════════════════════
           CONTACT SECTION — pure black background
         ══════════════════════════════════════════════ */}
      <section
        ref={(el) => (sections.current[1] = el)}
        id="contact"
        className="relative py-28 overflow-hidden opacity-0"
        style={{ background: '#000000' }}
      >
        <FloatingShapes />
        {/* Subtle glow blobs */}
        <motion.div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.07) 0%, transparent 65%)' }}
          animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 65%)' }}
          animate={{ scale: [1, 1.18, 1] }} transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 3 }} />

        {/* Top border accent */}
        <div className="absolute top-0 left-0 w-full h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.4), transparent)' }} />

        <div className="relative z-10 container mx-auto px-6 lg:px-20">

          {/* ── Section header ── */}
          <div className="text-center mb-20">
            <motion.span
              className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            >Get In Touch</motion.span>
            <motion.h2
              className="text-4xl md:text-5xl font-extrabold text-white mb-5"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            >Let's <span className="text-blue-400">Connect</span></motion.h2>
            <motion.p
              className="text-gray-400 text-lg max-w-xl mx-auto"
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
            >
              Have a project in mind? We'd love to hear about it. Drop us a message and we'll get back within 24 hours.
            </motion.p>
          </div>

          {/* ── Main grid: Info left, Form right ── */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

            {/* ── LEFT: Contact info + map (2 cols) ── */}
            <motion.div
              className="lg:col-span-2 space-y-6"
              initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            >

              {/* Contact detail cards */}
              {[
                {
                  emoji: '📍',
                  label: 'Office Address',
                  value: 'Narayan nagar main road, Rajkot, Gujarat, India',
                  color: 'rgba(37,99,235,0.12)',
                  border: 'rgba(59,130,246,0.25)',
                },
                {
                  emoji: '📞',
                  label: 'Phone Numbers',
                  value: '+91 81415 81103\n+91 87806 43219',
                  color: 'rgba(124,58,237,0.12)',
                  border: 'rgba(139,92,246,0.25)',
                  href: 'tel:+918141581103',
                },
                {
                  emoji: '✉️',
                  label: 'Email Addresses',
                  value: 'cmcoretech@gmail.com',
                  color: 'rgba(6,182,212,0.12)',
                  border: 'rgba(6,182,212,0.25)',
                  href: 'mailto:cmcoretech@gmail.com',
                },
                {
                  emoji: '💬',
                  label: 'WhatsApp',
                  value: 'Chat with us on WhatsApp',
                  color: 'rgba(22,163,74,0.12)',
                  border: 'rgba(22,163,74,0.25)',
                  href: 'https://wa.me/918141581103',
                },
              ].map((item, i) => (
                <motion.a
                  key={i}
                  href={item.href || undefined}
                  target={item.href?.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 rounded-2xl block transition-all duration-300"
                  style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(255,255,255,0.08)`, textDecoration: 'none' }}
                  initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 + i * 0.1 }}
                  whileHover={{ background: item.color, borderColor: item.border, transition: { duration: 0.2 } }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
                    style={{ background: item.color, border: `1px solid ${item.border}` }}
                  >{item.emoji}</div>
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-1">{item.label}</p>
                    {item.value.split('\n').map((line, li) => (
                      <p key={li} className="text-gray-200 text-sm font-medium">{line}</p>
                    ))}
                  </div>
                </motion.a>
              ))}

              {/* Google Map — Rajkot */}
              <motion.div
                className="rounded-2xl overflow-hidden"
                style={{ border: '1px solid rgba(59,130,246,0.2)' }}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3692.2190543354227!2d70.80180829999999!3d22.2696905!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3959ca701334b51b%3A0x3b5fe31ffe7f378d!2sOmkaran%20Furniture!5e0!3m2!1sen!2sin!4v1773151681239!5m2!1sen!2sin"
                  width="100%"
                  height="200"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                  title="CmCoreTech Location — Rajkot"
                />
              </motion.div>
            </motion.div>

            {/* ── RIGHT: Contact form (3 cols) ── */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            >
              <form
                method="POST"
                action="https://api.web3forms.com/submit"
                className="p-8 rounded-3xl space-y-6"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.09)' }}
              >
                <input type="hidden" name="access_key" value="200eb3f4-eb9a-4cbe-b4f6-b017c2fce186" />

                {/* Name + Email row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2">Your Name</label>
                    <input
                      type="text" name="name" required placeholder="Your full name"
                      className="w-full text-white text-sm rounded-xl px-4 py-3 outline-none transition-all duration-300"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.background = 'rgba(37,99,235,0.08)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2">Email Address</label>
                    <input
                      type="email" name="email" required placeholder="you@example.com"
                      className="w-full text-white text-sm rounded-xl px-4 py-3 outline-none transition-all duration-300"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.background = 'rgba(37,99,235,0.08)'; }}
                      onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2">Phone Number</label>
                  <input
                    type="tel" name="phone" placeholder="+91 98765 43210"
                    className="w-full text-white text-sm rounded-xl px-4 py-3 outline-none transition-all duration-300"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.background = 'rgba(37,99,235,0.08)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                  />
                </div>

                {/* Service interest */}
              

                {/* Message */}
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2">Your Message</label>
                  <textarea
                    name="message" required rows="5" placeholder="Tell us about your project, goals, or any questions..."
                    className="w-full text-white text-sm rounded-xl px-4 py-3 outline-none transition-all duration-300 resize-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    onFocus={e => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.background = 'rgba(37,99,235,0.08)'; }}
                    onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.05)'; }}
                  />
                </div>

                {/* Divider */}
                <div className="w-full h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

                {/* Submit */}
                <motion.button
                  type="submit"
                  className="w-full text-white font-bold py-3.5 rounded-xl text-sm tracking-wide transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}
                  whileHover={{ scale: 1.02, opacity: 0.92 }}
                  whileTap={{ scale: 0.98 }}
                >
                  🚀 Send Message
                </motion.button>

                {/* WhatsApp alternative */}
                <a
                  href="https://wa.me/918141581103?text=Hello, I have a project inquiry"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full text-sm font-semibold py-3 rounded-xl transition-all duration-300 text-gray-300 hover:text-white"
                  style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.25)' }}
                >
                  💬 Or reach us on WhatsApp
                </a>
              </form>
            </motion.div>
          </div>

        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-950 text-gray-300 py-6 text-center">
        <p>© 2025 CmCoreTech. All rights reserved.</p>
        <p className="mt-2">Contact: cmcoretech@gmail.com | +91 87806 43219</p>
      </footer>
      {/* ══ BACK TO TOP ══ */}
      <motion.button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: showTop ? 1 : 0, scale: showTop ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.15, boxShadow: '0 0 20px rgba(99,102,241,0.6)' }}
        whileTap={{ scale: 0.9 }}
        aria-label="Back to top"
      >
        <ChevronUp className="w-5 h-5 text-white" />
      </motion.button>

    </div>
  );
};

export default Contact;
